import { Injectable, Inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { SdmGlobal } from '../config/config';

export interface SdmUser {
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class Auth0Service {
  
  constructor(@Inject(AuthService) private auth0: AuthService) {}

  /**
   * Check if user is authenticated
   */
  public isAuthenticated$(): Observable<boolean> {
    return this.auth0.isAuthenticated$;
  }

  /**
   * Get current user
   */
  public getUser$(): Observable<SdmUser | null> {
    return this.auth0.user$ as Observable<SdmUser | null>;
  }

  /**
   * Get access token
   */
  public getAccessToken$(): Observable<string | undefined> {
    return this.auth0.getAccessTokenSilently().pipe(
      catchError((error) => {
        console.error('Error getting access token:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Login with redirect
   */
  public loginWithRedirect(options?: {
    connection?: string;
    redirectUri?: string;
    appState?: any;
  }): void {
    const loginOptions = {
      authorizationParams: {
        connection: options?.connection,
        redirect_uri: options?.redirectUri || window.location.origin,
        scope: 'openid email profile user_metadata'
      },
      appState: options?.appState
    };

    this.auth0.loginWithRedirect(loginOptions);
  }

  /**
   * Login for global admin
   */
  public loginGlobal(requestedUrl?: string): void {
    this.loginWithRedirect({
      connection: 'sdmglobal',
      redirectUri: window.location.origin + '/LoginGlobal',
      appState: { requestedUrl: requestedUrl || 'GlobalMain' }
    });
  }

  /**
   * Login for regular users
   */
  public loginRegular(requestedUrl?: string): void {
    this.loginWithRedirect({
      connection: SdmGlobal.auth0Connection(),
      redirectUri: window.location.origin + '/Login',
      appState: { requestedUrl: requestedUrl || 'Main' }
    });
  }

  /**
   * Logout
   */
  public logout(isGlobal: boolean = false): void {
    this.auth0.logout({
      logoutParams: {
        returnTo: window.location.origin + (isGlobal ? '/LoginGlobal' : '/Login')
      }
    });
  }

  /**
   * Handle authentication callback
   */
  public handleRedirectCallback$(): Observable<any> {
    return this.auth0.handleRedirectCallback().pipe(
      tap((result: any) => {
        
        // Navigate to requested URL from appState
        if (result && result.appState && result.appState.requestedUrl) {
          window.location.href = result.appState.requestedUrl;
        } else {
          // Default navigation based on user type
          this.getUser$().subscribe(user => {
            const isGlobal = this.isGlobalUser(user);
            window.location.href = isGlobal ? '/GlobalAdmin' : '/Main';
          });
        }
      }),
      catchError((error) => {
        console.error('Error handling Auth0 callback:', error);
        return of(null);
      })
    );
  }

  /**
   * Check if user is a global admin
   */
  private isGlobalUser(user: SdmUser | null): boolean {
    // Add your logic to determine if user is global admin
    // This might check user metadata, roles, or email domain
    return user?.email?.includes('@scanbur') || false;
  }

  /**
   * Get Auth0 configuration for this environment
   */
  public static getAuth0Config() {
    return {
      domain: 'login.sdmweb.scanburapps.com',
      clientId: SdmGlobal.auth0ClientId(),
      authorizationParams: {
        redirect_uri: window.location.origin,
        scope: 'openid email profile user_metadata'
      },
      useRefreshTokens: true,
      cacheLocation: 'localstorage' as const
    };
  }
}
